import React, {useCallback, useState, useRef, useEffect} from 'react';
import {
  View,
  Text,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionHeader,
} from '@packages/systems/spring';
import {useSpring, animated} from '@react-spring/web';
import {emotionStyled, keyframes} from '@packages/utilities/styled';
import {ReasoningText} from './reasoning-text';

export interface ReasoningArtifactProps {
  content: string;
  isComplete: boolean;
}

export const ReasoningArtifact = ({
  content,
  isComplete,
}: ReasoningArtifactProps) => {
  const [openIndices, setOpenIndices] = useState<number[]>([0]);
  const scrollRef = useRef<HTMLDivElement>(null);

  const handleAccordionChange = useCallback((indices: number[]) => {
    setOpenIndices(indices);
  }, []);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [content]);

  const isExpanded = openIndices.includes(0);

  const springStyles = useSpring({
    opacity: isExpanded ? 1 : 0,
    maxHeight: isExpanded ? 200 : 0,
    overflow: 'hidden',
    config: {friction: 15, tension: 90},
  });

  return (
    <Accordion
      isCollapsible
      allowMultiple={false}
      hasTopDivider={false}
      openIndices={openIndices}
      onChange={handleAccordionChange}
    >
      <AccordionItem>
        <AccordionHeader>
          <StyledAccordionButton>
            <ReasoningText isReasoning={!isComplete} />
          </StyledAccordionButton>
        </AccordionHeader>
        <animated.div style={springStyles as unknown as React.CSSProperties}>
          <ScrollContainer ref={scrollRef}>
            <ScrollIndicatorTop />
            <ScrollContent>
              <Text>{content}</Text>
            </ScrollContent>
            <ScrollIndicatorBottom />
          </ScrollContainer>
        </animated.div>
      </AccordionItem>
    </Accordion>
  );
};

const StyledAccordionButton = emotionStyled(AccordionButton)({
  marginTop: '$1',
  marginBottom: '$2',
  height: '$3',
  marginLeft: '-$2',
  width: 'fit-content',
  justifyContent: 'flex-start',
});

const ScrollContainer = emotionStyled(View)({
  marginBottom: '$1',
  borderRadius: '$2',
  backgroundColor: '$background2',
  overflowY: 'auto',
  maxHeight: '200px',
  position: 'relative',
});

const ScrollContent = emotionStyled(View)({
  padding: '$2',
});

const revealTop = keyframes({
  to: {
    transform: 'scaleY(1)',
  },
});

const revealBottom = keyframes({
  to: {
    transform: 'scaleY(0)',
  },
});

const ScrollIndicatorTop = emotionStyled('div')({
  position: 'sticky',
  top: 0,
  left: 0,
  right: 0,
  height: '$6',
  marginBottom: '-$6',
  zIndex: 1,
  pointerEvents: 'none',
  background: 'linear-gradient(to bottom, $background2, transparent)',
  transformOrigin: 'top center',
  transform: 'scaleY(0)',
  animation: `${revealTop} linear forwards`,
  animationTimeline: 'scroll(nearest)',
  animationRange: '0 2rem',
});

const ScrollIndicatorBottom = emotionStyled('div')({
  position: 'sticky',
  bottom: 0,
  left: 0,
  right: 0,
  height: '$6',
  marginTop: '-$6',
  zIndex: 1,
  pointerEvents: 'none',
  background: 'linear-gradient(to top, $background2, transparent)',
  transformOrigin: 'bottom center',
  transform: 'scaleY(1)',
  animation: `${revealBottom} linear forwards`,
  animationTimeline: 'scroll(nearest)',
  animationRange: 'calc(100% - 2rem) 100%',
});
